const UNDEFINED_LOCALE: string = 'und';
// Map of deprecated/unsupported language tags pointing to their modern/supported counterparts.
const SPECIAL_CASE_LANGUAGES = new Map([
    ['no', 'nb'],
    ['iw', 'he'],
    ['in', 'id'],
    ['ji', 'yi']
]);
const DEFAULT_NUMBERING_SYSTEM: string = 'latn';

export class LocaleUtil {

    /**
     * Get the canonical unicode locale string identifier(s) for the given locale string(s). For more on valid locale
     * string values, check out: 
     * https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl#locales_argument
     *
     * This method provides some leniency for locale string inputs (e.g., trims any trailing whitespaces, replaces '_' or 
     * '-') over the acceptable locale string values documented in: 
     * https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl#locales_argument 
     *
     * This method always sets the locale to use Latin digit shape (unless locale already contains number shape override
     * (e.g., th_TH-u-nu-thai).
     *
     * @param locales A single locale string or a locale string array.
     * @return A single canonical locale string or an array of canonical locale strings.
     */
    public static getCanonicalLocaleString(locales?: string | string[]): string | string[] {
        if (Array.isArray(locales)) {
            return locales.map((locale) => LocaleUtil.getCanonicalLocale(
                    LocaleUtil.forLocaleStringInternal(locale)).toLocaleString())
        }
        return locales = LocaleUtil.getCanonicalLocale(
                LocaleUtil.forLocaleStringInternal(locales)).toLocaleString()
    }

    /**
     * Get the canonicalized locale for the given locale; this does an undefined check and replaces it with the default
     * runtime locale.
     *
     * This method always sets the locale to use Latin digit shape (unless locale already contains number shape override
     * (e.g., th_TH-u-nu-thai).
     *
     * @param locale Intl.Locale object to canonicalize.
     * @return canonicalized Intl.Locale for the given locale.
     */
    // @ts-ignore
    public static getCanonicalLocale(locale?: Intl.Locale): Intl.Locale {
        if (locale == undefined) {
            // Fetch the default runtime locale
            locale = new Intl.Locale(new Intl.NumberFormat().resolvedOptions().locale);
        }

        locale = LocaleUtil.processLocale(locale);

        if (locale.numberingSystem) {
            // If a numbering system is already present, we do not change the locale.
            return locale as Intl.Locale;
        }

        // Set default numbering system on the input locale.
        locale = new Intl.Locale(locale, { numberingSystem: DEFAULT_NUMBERING_SYSTEM });
        return locale;
    }

    private static processLocale(intlLocale: Intl.Locale): Intl.Locale {
        if (intlLocale.toLocaleString().startsWith(UNDEFINED_LOCALE)) {
            return intlLocale;
        }

        // Check whether the locale is supported.
        const supportedLocales: string[] = Intl.NumberFormat.supportedLocalesOf(intlLocale.toLocaleString());

        // If locale is not supported return undefined locale.
        if (supportedLocales.length === 0) {
            // Check for exempted locales.
            const specialLanguage = SPECIAL_CASE_LANGUAGES.get(intlLocale.language);
            if (specialLanguage) {
                return new Intl.Locale(intlLocale.baseName.replace(intlLocale.language, specialLanguage));
            }
            console.log(`Unsupported locale: ${intlLocale.toLocaleString()}`);
            return new Intl.Locale(UNDEFINED_LOCALE);
        }
        return new Intl.Locale(supportedLocales[0]);
    }

    /**
     * Given the locale string identifier, returns the corresponding Intl.Locale instance.
     *
     * Provides some leniency for locale string inputs over the acceptable locale string values documented in: 
     * https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl#locales_argument:
     * 
     * * Trims any trailing whitespaces
     * * replaces all occurrences of '_' with '-'.
     */
    private static forLocaleStringInternal(locale?: string): Intl.Locale | undefined {
        return locale ? new Intl.Locale(locale.trim().replace(/_/g, '-')) : undefined;
    }
}
